import { createContext, useContext, useMemo } from 'react';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import {
  LuArrowLeftRight, LuArrowUpRight, LuCheck, LuChevronRight, LuFileText, LuPencil, LuReceipt,
  LuTarget,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { GOAL_ICONS } from '../goals/GoalIcon';
import { FALLBACK_GOAL_COLOR, hexToTint } from '../../utils/goals';
import { formatFigure } from '../../utils/formatters';
import { dayShort, formatDateShort, formatDayShort } from '../../utils/intlFormatters';
import { getAccountDisplay, isPrimaryTextRestricted } from '../../utils/movementDisplay';
import { useChatRecord, useChatRecords } from './ChatRecordsContext';
import {
  recordDestination, recordKey, recordPath, searchOfChange, searchOfChangeIn,
} from './chatRecords';

/**
 * How the chat draws a record (MI-343): a chip inside a sentence, a row in a
 * card, a receipt for what the chat created or changed.
 *
 * Each one is given a type and an id and reads the rest from the app
 * (`useChatRecord`), so it shows the record as it is now. A record that no
 * longer exists keeps its text and loses its link.
 */

// What the backend kept with one message: the numbers a tool computed for that
// answer, what was done to a record, and the searches its rows came from.
const MessageContext = createContext({ byKey: new Map(), searches: [], position: null });

/** `position` is the message's place in the conversation, when it has one. */
export function MessageRecords({ records, searches, position = null, children }) {
  const value = useMemo(() => ({
    byKey: new Map((records || []).map((r) => [recordKey(r.type, r.id), r])),
    searches: searches || [],
    position,
  }), [records, searches, position]);
  return <MessageContext.Provider value={value}>{children}</MessageContext.Provider>;
}

const useMessageRecord = (type, id) => useContext(MessageContext).byKey.get(recordKey(type, id));

const last4 = (account) => (
  account?.external_account_number ? String(account.external_account_number).slice(-4) : null
);

/** The record's own name, or the assistant's words for it until it is read. */
const nameOf = (type, data, fallback) => {
  if (!data) return fallback;
  if (type === 'source_file') return data.filename || fallback;
  return data.name || fallback;
};

/** A record's mark: the icon it has in its own page. `size` is the box, in px. */
function Glyph({ type, data, size, round = false }) {
  const box = {
    w: `${size}px`, h: `${size}px`, flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: round ? 'full' : `${Math.round(size * 0.3)}px`,
    fontSize: `${Math.round(size * 0.58)}px`, lineHeight: 1,
  };
  if (type === 'goal') {
    // GoalIcon's own disc is a block, and a chip sits inside a sentence.
    const color = data?.color || FALLBACK_GOAL_COLOR;
    const Icon = GOAL_ICONS[data?.icon] || LuTarget;
    return (
      <Box as="span" {...box} bg={hexToTint(color)} color={color}>
        <Icon size={Math.round(size * 0.58)} />
      </Box>
    );
  }
  if (type === 'movement') {
    const category = data?.category;
    if (category?.icon) {
      return <Box as="span" {...box} bg={hexToTint(category.color, 0.18)}>{category.icon}</Box>;
    }
    const Icon = data?.type === 'Transfer' ? LuArrowLeftRight : LuReceipt;
    return (
      <Box as="span" {...box} bg="redesign.bg.card" color="redesign.text.muted">
        <Icon size={Math.round(size * 0.55)} />
      </Box>
    );
  }
  if (type === 'category') {
    return (
      <Box as="span" {...box} bg={data?.color ? hexToTint(data.color, 0.18) : 'redesign.bg.card'}>
        {data?.icon || (data?.name || '').charAt(0)}
      </Box>
    );
  }
  if (type === 'account') {
    const emoji = data?.icon || '🏦';
    return (
      <Box as="span" {...box} borderRadius="full" bg="redesign.bg.card" overflow="hidden">
        {data?.source_logo_url ? (
          <Image
            src={data.source_logo_url} alt="" boxSize={`${Math.round(size * 0.62)}px`}
            objectFit="contain" fallback={<span>{emoji}</span>}
          />
        ) : emoji}
      </Box>
    );
  }
  if (type === 'tag') {
    const dot = Math.max(7, Math.round(size * 0.38));
    return (
      <Box as="span" {...box} bg={size > 18 ? 'redesign.bg.card' : 'transparent'}>
        <Box as="span" w={`${dot}px`} h={`${dot}px`} borderRadius="full" bg={data?.color || '#3182CE'} />
      </Box>
    );
  }
  return (
    <Box as="span" {...box} bg={size > 18 ? 'redesign.bg.card' : 'transparent'} color="redesign.text.muted">
      <LuFileText size={Math.round(size * 0.6)} />
    </Box>
  );
}

const open = (event, handler, ...args) => {
  // A modified click is the browser's: open in a new tab, as any link does.
  if (!handler || event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
  event.preventDefault();
  handler(...args);
};

/**
 * Where a click on a record goes (MI-346). A category the answer summed for a
 * month opens on that month; for a longer period, Movements filtered to it.
 * The href is what a modified click opens in a new tab.
 */
const useDestination = (type, id) => {
  const { onOpenRecord, onOpenSearch, phone } = useChatRecords();
  const { month, search } = recordDestination(useMessageRecord(type, id), { phone });
  return {
    href: recordPath(type, id, { month }),
    onClick: (event) => (search && onOpenSearch
      ? open(event, onOpenSearch, search)
      : open(event, onOpenRecord, type, id, { month })),
  };
};

const FOCUS = {
  outline: 'none', borderColor: 'redesign.accent.blue',
  boxShadow: '0 0 0 1px var(--chakra-colors-redesign-accent-blue)',
};

/**
 * A record inside a sentence or a table cell. `children` is what the assistant
 * wrote; the chip shows the record's own name once it is read. `data` skips the
 * lookup for a caller that already holds the record.
 */
export function RecordChip({ type, id, data: given, children }) {
  const { openKey, phone } = useChatRecords();
  const destination = useDestination(type, id);
  const isOpen = openKey === recordKey(type, id);
  const found = useChatRecord(type, id);
  const { status, data } = given ? { status: 'ready', data: given } : found;
  const box = {
    display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle',
    gap: phone ? '6px' : '5px', h: phone ? '26px' : '22px', maxW: '100%',
    pl: phone ? '4px' : '3px', pr: phone ? '9px' : '7px',
    borderWidth: '1px', borderRadius: phone ? '9px' : '7px',
    fontSize: phone ? '15px' : '12px', fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap',
  };
  const label = (
    <Box as="span" overflow="hidden" textOverflow="ellipsis">{nameOf(type, data, children)}</Box>
  );

  if (status === 'missing') {
    return (
      <Box
        as="span" {...box} borderStyle="dashed" borderColor="redesign.text.faint"
        color="redesign.text.muted" textDecoration="line-through" data-record-gone
      >
        {label}
      </Box>
    );
  }
  return (
    <Box
      as="a" href={destination.href} {...box}
      borderColor={isOpen ? 'phone.newPillBg' : 'redesign.border'}
      bg={isOpen ? 'redesign.bg.accent' : 'redesign.bg.surface'}
      color={isOpen ? 'phone.newPillFg' : 'redesign.heading'}
      textDecoration="none" transition="background 0.12s"
      _hover={isOpen ? { textDecoration: 'none' } : {
        bg: 'redesign.bg.bubble', borderColor: 'redesign.text.faint', textDecoration: 'none',
      }}
      _focusVisible={FOCUS}
      onClick={destination.onClick}
      aria-current={isOpen || undefined}
      data-record={recordKey(type, id)}
    >
      <Glyph type={type} data={data} size={phone ? 18 : 16} round={type === 'account'} />
      {label}
      {type === 'account' && last4(data) && (
        <Box as="span" fontWeight={400} color="redesign.text.muted">{last4(data)}</Box>
      )}
    </Box>
  );
}

const weekdayOf = (ymd) => {
  const [y, m, d] = String(ymd || '').slice(0, 10).split('-').map(Number);
  return y && m && d ? dayShort(new Date(y, m - 1, d).getDay()) : '';
};

const isToday = (ymd) => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return String(ymd || '').slice(0, 10)
    === `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// The fields whose values are the backend's own words, shown in the reader's.
// Every other value is the user's data (a category's name), shown as it is.
const WORDED_VALUES = ['is_active', 'status', 'saving_mode'];
const NAMED_EMPTY = ['category', 'tags', 'goal', 'group'];

/** One value of a change, in words: "Sin categoría", "activa", "2 partes". */
function changeValue(t, field, value) {
  if (value == null) return t(NAMED_EMPTY.includes(field) ? `records.empty.${field}` : 'records.empty.default');
  if (field === 'merged' || field === 'split') return t(`records.value.${field}`, { count: value });
  return WORDED_VALUES.includes(field) ? t(`records.value.${field}.${value}`, { defaultValue: value }) : value;
}

/** "before → after" of the first change, with "+N" when more than one moved. */
function ChangeText({ record, withField }) {
  const { t } = useTranslation('chat');
  const [first, ...rest] = record.changes || [];
  if (!first) return null;
  if (first.field === 'ignored') {
    return <>{t(first.after === 'true' ? 'records.updated.ignored' : 'records.updated.restored')}</>;
  }
  const field = t(`records.field.${first.field}`, { defaultValue: first.field });
  return (
    <>
      {withField ? `${field}: ` : ''}
      {!['merged', 'split'].includes(first.field) && (
        <>
          <Box as="span" textDecoration="line-through">{changeValue(t, first.field, first.before)}</Box>
          {' → '}
        </>
      )}
      {changeValue(t, first.field, first.after)}
      {rest.length > 0 ? ` +${rest.length}` : ''}
    </>
  );
}

/** What a row says about its record: title, line under it, figure on the right. */
function useRowText(type, data, record, label) {
  const { t } = useTranslation(['chat', 'movements']);
  const { phone, homeCurrency } = useChatRecords();
  const count = record?.count != null
    ? t(record.count === 1 ? 'records.movementsOne' : 'records.movementsOther', { count: record.count })
    : null;
  const figure = (amount, currency) => (amount == null ? null : {
    code: phone && currency === homeCurrency ? null : currency,
    text: `${Number(amount) < 0 ? '−' : ''}${formatFigure(amount)}`,
  });
  const typeLabel = t(`records.type.${type}`);
  // "Gasto" for a movement or a category; '' for a type the app has no word for.
  const kind = (value) => (value
    ? t(`movements:types.${String(value).toLowerCase()}`, { defaultValue: '' })
    : '');
  const created = record?.action === 'created';
  const changed = record?.action === 'updated' && record.changes?.length > 0;

  if (type === 'movement') {
    const restricted = isPrimaryTextRestricted(data);
    const day = formatDayShort(String(data.date || '').slice(0, 10));
    const account = getAccountDisplay(data);
    const today = isToday(data.date) ? t('history.today') : null;
    let subtitle = phone ? `${account} · ${weekdayOf(data.date)}, ${day}` : `${day} · ${account}`;
    if (changed) {
      subtitle = phone ? <ChangeText record={record} /> : <>{day} · <ChangeText record={record} /></>;
    } else if (created) {
      // Its account is on the line under it: the row says when and what.
      const when = phone ? (today || day) : [today, day].filter(Boolean).join(', ');
      subtitle = [when, kind(data.type)].filter(Boolean).join(' · ');
    }
    return {
      title: restricted ? t('records.restricted') : (data.note || data.description || label || typeLabel),
      muted: restricted,
      subtitle,
      figure: figure(data.amount, data.currency),
    };
  }
  // What the record is, beyond its type: "Gasto", "Tarjeta de crédito".
  const about = () => {
    if (type === 'account') {
      return data.account_type ? t(`records.accountType.${data.account_type}`, { defaultValue: '' }) : '';
    }
    if (type === 'category') return kind(data.type);
    if (type === 'goal' && created && data.target_amount != null) {
      return [data.currency, formatFigure(data.target_amount)].filter(Boolean).join(' ');
    }
    if (type === 'source_file') {
      return [String(data.file_type || '').toUpperCase(), formatDateShort(data.upload_date)]
        .filter(Boolean).join(' · ');
    }
    return '';
  };
  let subtitle;
  if (changed) {
    subtitle = <>{typeLabel} · <ChangeText record={record} withField /></>;
  } else if (created) {
    // A record the chat just made says what it is first: "Categoría · Gasto".
    subtitle = [typeLabel, about()].filter(Boolean).join(' · ');
  } else {
    subtitle = [about(), count].filter(Boolean).join(' · ') || typeLabel;
  }
  // A goal's row is its progress, as on its page; a figure beside it repeats it.
  const pct = type === 'goal' && !changed && !created ? record?.pct ?? null : null;
  // A category's total comes summed with its sign, spending below zero. Its
  // page says how much was spent, as a positive figure, and so does its row.
  const amount = type === 'category' && data.type === 'Expenses' && record?.amount != null
    ? -Number(record.amount)
    : record?.amount;
  return {
    title: nameOf(type, data, label),
    extra: type === 'account' ? last4(data) : null,
    subtitle,
    figure: created || pct != null ? null : figure(amount, record?.currency),
    pct,
  };
}

function RowBody({ type, id, data, label, chevron, isOpen }) {
  const { t } = useTranslation('chat');
  const { phone } = useChatRecords();
  const record = useMessageRecord(type, id);
  const { title, extra, muted, subtitle, figure, pct } = useRowText(type, data, record, label);
  const small = phone ? '13px' : '11px';
  return (
    <>
      <Glyph type={type} data={data} size={phone ? 40 : 24} round={phone || type === 'account'} />
      <Flex direction="column" flex="1" minW={0} gap={pct != null ? '3px' : 0}>
        <Text
          as="span" fontSize={phone ? '16px' : '12px'} lineHeight={phone ? '22px' : '16px'}
          fontWeight={isOpen ? 600 : 500} fontStyle={muted ? 'italic' : 'normal'}
          color={isOpen ? 'phone.newPillFg' : muted ? 'redesign.text.muted' : 'redesign.heading'}
          isTruncated
        >
          {title}
          {extra && <Box as="span" ml={1} fontWeight={400} color="redesign.text.muted">{extra}</Box>}
        </Text>
        {pct != null ? (
          <Box h="4px" borderRadius="full" bg="redesign.bg.sunken" overflow="hidden">
            <Box h="100%" w={`${Math.min(Math.max(pct, 0), 100)}%`} borderRadius="full" bg="redesign.accent.blue" />
          </Box>
        ) : (
          <Text
            as="span" fontSize={small} lineHeight={phone ? '18px' : '15px'}
            color={phone ? 'phone.muted' : 'redesign.text.muted'} isTruncated
          >
            {subtitle}
          </Text>
        )}
      </Flex>
      {record?.action === 'created' && type !== 'movement' && (
        <Box
          as="span" display="inline-flex" alignItems="center" h="18px" px="6px" borderRadius="full"
          bg="rgba(21, 128, 61, 0.10)" _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }} color="redesign.amount.income" fontSize="10px" fontWeight={600}
          flexShrink={0}
        >
          {t('records.newBadge')}
        </Box>
      )}
      {pct != null && (
        <Text as="span" fontSize={phone ? '16px' : '12px'} fontWeight={500} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {pct}%
        </Text>
      )}
      {figure && (
        <Text
          as="span" fontSize={phone ? '16px' : '12px'} fontWeight={phone ? 600 : 500} whiteSpace="nowrap"
          color="redesign.heading" sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {figure.code && (
            <Box as="span" fontSize={phone ? '13px' : '10px'} fontWeight={400} color="redesign.text.muted" mr="3px">
              {figure.code}
            </Box>
          )}
          {figure.text}
        </Text>
      )}
      {chevron && <Box as={LuChevronRight} boxSize={phone ? '20px' : '14px'} color="redesign.text.muted" flexShrink={0} />}
    </>
  );
}

/** A record as a row of a card. */
function Row({ type, id, label, chevron = false }) {
  const { t } = useTranslation('chat');
  const { openKey, phone } = useChatRecords();
  const destination = useDestination(type, id);
  const isOpen = openKey === recordKey(type, id);
  const { status, data } = useChatRecord(type, id);
  const box = {
    display: 'flex', alignItems: 'center', gap: phone ? '12px' : '10px',
    minH: phone ? '60px' : '44px', px: phone ? '14px' : '8px', py: phone ? '8px' : '6px',
    borderRadius: phone ? 0 : '8px', color: 'redesign.heading',
  };
  if (status !== 'ready') {
    return (
      <Box {...box} color="redesign.text.muted" data-record-gone={status === 'missing' || undefined}>
        <Glyph type={type} data={null} size={phone ? 40 : 24} round={phone} />
        <Flex direction="column" flex="1" minW={0}>
          <Text
            as="span" fontSize={phone ? '16px' : '12px'} isTruncated
            textDecoration={status === 'missing' ? 'line-through' : 'none'}
          >
            {label || t(`records.type.${type}`)}
          </Text>
          {status === 'missing' && (
            <Text as="span" fontSize={phone ? '13px' : '11px'}>{t('records.gone')}</Text>
          )}
        </Flex>
      </Box>
    );
  }
  return (
    <Box
      as="a" href={destination.href} {...box} textDecoration="none"
      bg={isOpen ? 'redesign.bg.accent' : undefined}
      _hover={{ bg: phone || isOpen ? undefined : 'redesign.bg.bubble', textDecoration: 'none' }}
      _active={{ bg: 'redesign.bg.bubble' }}
      _focusVisible={{ ...FOCUS, borderRadius: '8px' }}
      onClick={destination.onClick}
      aria-current={isOpen || undefined}
      data-record={recordKey(type, id)}
    >
      <RowBody type={type} id={id} data={data} label={label} chevron={chevron} isOpen={isOpen} />
    </Box>
  );
}

/** The card's last line: opens Movements on the search the rows came from. */
function SeeAll({ count, search }) {
  const { t } = useTranslation('chat');
  const { onOpenSearch, phone } = useChatRecords();
  if (!onOpenSearch) return null;
  return (
    <Flex
      as="button" type="button" w="100%" align="center" justify="space-between"
      minH={phone ? '48px' : '36px'} px={phone ? '14px' : '12px'}
      borderTopWidth="1px" borderColor="redesign.border"
      fontSize={phone ? '15px' : '12px'} fontWeight={500} color="phone.link" textAlign="left"
      _hover={{ bg: 'redesign.bg.bubble' }} _focusVisible={FOCUS}
      onClick={() => onOpenSearch(search)}
      data-see-all
    >
      {t(count === 1 ? 'records.seeAllInMovementsOne' : 'records.seeAllInMovementsOther', { count })}
      <Box as={LuArrowUpRight} boxSize={phone ? '18px' : '14px'} />
    </Flex>
  );
}

function Card({ header, tone, children, footer }) {
  const { phone } = useChatRecords();
  const HeaderIcon = tone === 'created' ? LuCheck : LuPencil;
  return (
    <Box
      my={2} borderWidth="1px" borderColor="redesign.border" overflow="hidden"
      borderRadius={phone ? '14px' : '12px'} bg={phone ? 'redesign.bg.card' : 'redesign.bg.surface'}
      boxShadow={phone ? 'none' : 'card'}
      sx={phone ? { '& > [data-rows] > * + *': { borderTopWidth: '1px', borderColor: 'redesign.border' } } : undefined}
      data-record-card
    >
      {header && (
        <Flex
          align="center" gap="6px" px={phone ? '14px' : '12px'} pt={phone ? '10px' : '8px'}
          textStyle="microLabel"
          color={tone === 'created' ? 'redesign.amount.income' : 'redesign.text.muted'}
        >
          <HeaderIcon size={12} strokeWidth={2.5} />
          {header}
        </Flex>
      )}
      <Flex direction="column" p={phone ? 0 : '4px'} data-rows>{children}</Flex>
      {footer}
    </Box>
  );
}

/**
 * Records the assistant listed, one per line, drawn as rows. When they are
 * movements of one search that found more than are listed, the card ends with
 * the link to all of them.
 */
export function RecordRows({ items }) {
  const { byKey, searches } = useContext(MessageContext);
  const from = items.map((item) => byKey.get(recordKey(item.type, item.id))?.search);
  const search = from.every((index) => index != null && index === from[0]) ? searches[from[0]] : null;
  return (
    <Card footer={search && search.count > items.length ? <SeeAll count={search.count} search={search} /> : null}>
      {items.map((item) => <Row key={recordKey(item.type, item.id)} {...item} />)}
    </Card>
  );
}

const idOf = (related, fallback) => related?.id ?? fallback ?? null;

/** Under a movement the chat created: where it was filed. */
function Filed({ id }) {
  const { t } = useTranslation('chat');
  const { phone } = useChatRecords();
  const { status, data } = useChatRecord('movement', id);
  if (status !== 'ready') return null;
  const tags = (data.tag_movements || []).map((tm) => tm.tag).filter(Boolean);
  const lines = [
    ['account', data.account ? [data.account] : []],
    ['category', data.category ? [data.category] : []],
    ['tags', tags],
  ].filter(([, list]) => list.length > 0);
  if (lines.length === 0) return null;
  return (
    <Flex
      direction="column" gap={phone ? 0 : '6px'} px={phone ? '14px' : '12px'}
      pt={phone ? '4px' : '10px'} pb={phone ? '8px' : '12px'}
      borderTopWidth="1px" borderColor="redesign.border"
    >
      {lines.map(([key, list]) => (
        <Flex key={key} align="center" gap={phone ? '12px' : '8px'} minH={phone ? '40px' : undefined}>
          <Text
            as="span" w={phone ? '84px' : '68px'} flexShrink={0}
            fontSize={phone ? '15px' : '12px'} color={phone ? 'phone.muted' : 'redesign.text.muted'}
          >
            {t(key === 'tags' && list.length === 1 ? 'records.detail.tag' : `records.detail.${key}`)}
          </Text>
          <Flex gap="4px" wrap="wrap" minW={0}>
            {list.map((related) => (
              <RecordChip
                key={related.id}
                type={key === 'tags' ? 'tag' : key}
                id={idOf(related)}
                data={related}
              />
            ))}
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
}

/** The header of a card of changed movements: "Categoría cambiada". */
function changeHeader(t, i18n, movements) {
  const fields = new Set(movements.flatMap((m) => (m.changes || []).map((c) => c.field)));
  const count = movements.length;
  const [only] = fields;
  if (fields.size === 1 && only === 'ignored') {
    const ignored = movements.every((m) => m.changes[0].after === 'true');
    return t(ignored ? 'records.updated.ignored' : 'records.updated.restored');
  }
  // One field moved: the card is named after it, when the app has the words.
  const named = fields.size === 1
    ? [`records.changed.${only}`, `records.updated.${only}`].find((key) => i18n.exists(`chat:${key}`))
    : null;
  return named
    ? t(named)
    : t(count === 1 ? 'records.updated.movementOne' : 'records.updated.movementOther', { count });
}

/**
 * What the chat created or changed in one message, drawn by the app from what
 * the tools reported, whatever the assistant wrote about it: created movements,
 * changed movements, then every other record.
 */
export function Receipts({ records }) {
  const { t, i18n } = useTranslation('chat');
  const { messages, onOpenSearch } = useChatRecords();
  const { searches, position } = useContext(MessageContext);
  if (!records?.length) return null;
  const movements = (action) => records.filter((r) => r.type === 'movement' && r.action === action);
  const created = movements('created');
  const updated = movements('updated');
  const others = records.filter((r) => r.type !== 'movement');
  // In Movements, with the filters the chat found them with, in this message or
  // an earlier one, when those still list exactly them. No link otherwise:
  // each row opens its movement.
  const searchOf = (list) => (position == null
    ? searchOfChange(list, searches)
    : searchOfChangeIn(messages, position, list));
  const seeAll = (list) => {
    const search = list.length > 1 && onOpenSearch ? searchOf(list) : null;
    return search ? <SeeAll count={list.length} search={search} /> : null;
  };
  return (
    <>
      {created.length > 0 && (
        <Card
          tone="created"
          header={t(created.length === 1 ? 'records.created.movementOne' : 'records.created.movementOther', { count: created.length })}
          footer={created.length === 1 ? <Filed id={created[0].id} /> : seeAll(created)}
        >
          {created.map((r) => <Row key={r.id} type="movement" id={r.id} chevron={created.length === 1} />)}
        </Card>
      )}
      {updated.length > 0 && (
        <Card tone="updated" header={changeHeader(t, i18n, updated)} footer={seeAll(updated)}>
          {updated.map((r) => <Row key={r.id} type="movement" id={r.id} />)}
        </Card>
      )}
      {others.length > 0 && (
        <Card>
          {others.map((r) => <Row key={recordKey(r.type, r.id)} type={r.type} id={r.id} chevron />)}
        </Card>
      )}
    </>
  );
}
